import React, { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HCHeatmap from 'highcharts/modules/heatmap';
import HCExporting from 'highcharts/modules/exporting';
import { useTheme } from '@mui/material/styles';

HCHeatmap(Highcharts);
// HCExporting(Highcharts);

const SEVERITY_COLORS = {
  Critical: '#c72929',
  Major: '#c72929',
  Medium: '#f47f1f',
  Minor: '#ffb300',
  Trivial: '#4cae4f',
  Information: '#1975d2',
  Unknown: 'transparent',
};

const SEVERITY_ORDER = [
  'Critical',
  'Major',
  'Medium',
  'Minor',
  'Trivial',
  'Information',
  'Unknown',
];

const getDayKey = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d.toISOString().split('T')[0];
};

const getHighestSeverity = (severities) => {
  for (const level of SEVERITY_ORDER) {
    if (severities.includes(level)) return level;
  }
  return 'Unknown';
};

const AlarmCalendarHeatmap = ({ alarms }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const chartOptions = useMemo(() => {
    const perDay = {};

    alarms.forEach((alarm) => {
      const key = getDayKey(alarm.Created);
      if (!perDay[key]) perDay[key] = { count: 0, severities: [] };
      perDay[key].count += 1;
      perDay[key].severities.push(alarm.Severity || 'Unknown');
    });

    const allDates = Object.keys(perDay).sort();
    if (allDates.length === 0) return {};

    const startDate = new Date(allDates[0]);
    const endDate = new Date(allDates[allDates.length - 1]);

    const data = [];

    const day = new Date(startDate);
    while (day <= endDate) {
      const key = getDayKey(day);
      const dayOfWeek = day.getDay();
      const weekNumber = Math.floor(
        (day - startDate) / (7 * 24 * 60 * 60 * 1000),
      );
      const entry = perDay[key];

      const count = entry?.count || 0;
      const severities = entry?.severities || [];
      const severity = entry ? getHighestSeverity(severities) : 'Unknown';
      const color = SEVERITY_COLORS[severity] || SEVERITY_COLORS.Unknown;

      data.push({
        x: weekNumber,
        y: dayOfWeek,
        value: count,
        color,
        custom: {
          date: new Date(day),
          severity,
          severities: [...new Set(severities)],
        },
      });

      day.setDate(day.getDate() + 1);
    }

    return {
      chart: {
        type: 'heatmap',
        height: 150,
        backgroundColor: isDark ? '#121212' : '#ffffff',
      },
      title: {
        text: '',
        style: {
          fontSize: '14px',
          color: isDark ? '#ffffff' : '#000000',
        },
      },
      xAxis: {
        type: 'datetime',
        tickmarkPlacement: 'on',
        labels: {
          formatter: function () {
            const base = new Date(startDate);
            base.setDate(base.getDate() + this.value * 7);
            const month = base.toLocaleString('default', { month: 'short' });
            const year = base.getFullYear();
            return `${month} ${year}`;
          },
          style: {
            color: isDark ? '#eeeeee' : '#333333',
          },
        },
        tickLength: 0,
        lineColor: isDark ? '#888' : '#ccc',
      },
      yAxis: {
        categories: [
          'Sunday',
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
          'Friday',
          'Saturday',
        ],
        title: { text: '' },
        reversed: true,
        gridLineWidth: 0,
        labels: {
          style: {
            color: isDark ? '#eeeeee' : '#333333',
          },
        },
      },
      colorAxis: { visible: false, stops: [] },
      tooltip: {
        backgroundColor: isDark ? '#333' : '#fff',
        style: { color: isDark ? '#fff' : '#000' },
        formatter() {
          const { date, severity, severities } = this.point.custom;
          return `
            <b>${date.toDateString()}</b><br/>
            Total alarms: <b>${this.point.value}</b><br/>
            Highest severity: <b>${severity}</b><br/>
            All severities: <b>${severities.join(', ') || 'None'}</b>
          `;
        },
      },
      series: [
        {
          name: 'Alarms',
          borderWidth: 1,
          data,
          dataLabels: { enabled: false },
          borderColor: isDark ? '#444' : '#f5f5f5',
        },
      ],
    };
  }, [alarms, theme.palette.mode]);

  return (
    <div style={{ maxWidth: '100%', overflowX: 'auto', mt: 1 }}>
      <HighchartsReact highcharts={Highcharts} options={chartOptions} />
    </div>
  );
};

export default AlarmCalendarHeatmap;
